import { SideNav } from "@pej/kore/components";
import type { SideNavGroup } from "@pej/kore/types";
import { Link, useRouterState } from "@tanstack/react-router";
import { Gauge, LayoutTemplate, MonitorPlay, ScrollText, Store } from "lucide-react";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { WorkspacePicker } from "./workspace-picker";

// This app has exactly two sections (the brief's simplification — no
// per-feature config needed until a third section exists).
export function Sidebar() {
    const { workspaceId } = useWorkspaceContext();
    // kore never reads the URL itself — it styles whatever `active` the
    // consumer computes. A descendant route (e.g. a future location detail page)
    // keeps the Locations row highlighted via the startsWith branch.
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

    const overviewHref = `/w/${workspaceId}`;
    const screensHref = `/w/${workspaceId}/screens`;
    const locationsHref = `/w/${workspaceId}/locations`;
    const templatesHref = `/w/${workspaceId}/templates`;
    const auditHref = `/w/${workspaceId}/audit`;

    // Two-group nav (Eric's operational/configuration split): the daily
    // surfaces up top, the visit-twice-a-year plumbing below.
    const groups: SideNavGroup[] = [
        {
            label: "Operate",
            items: [
                {
                    key: "overview",
                    label: "Overview",
                    icon: <Gauge className="size-4" />,
                    href: overviewHref,
                    // Exact only — startsWith would light it up on every page.
                    active: pathname === overviewHref,
                },
                {
                    key: "screens",
                    label: "Screens",
                    icon: <MonitorPlay className="size-4" />,
                    href: screensHref,
                    active: isActive(screensHref),
                },
                {
                    // The sibling vouchers admin gives the audit log a group
                    // of its own, named "Operations". Here it joins "Operate"
                    // instead: that group already carries the verb form of
                    // the same word, and a literal "Operations" beside it
                    // would read as a distinction without a difference.
                    key: "audit",
                    label: "Audit log",
                    icon: <ScrollText className="size-4" />,
                    href: auditHref,
                    active: isActive(auditHref),
                },
            ],
        },
        {
            label: "Configure",
            items: [
                {
                    key: "locations",
                    label: "Locations",
                    icon: <Store className="size-4" />,
                    href: locationsHref,
                    active: isActive(locationsHref),
                },
                {
                    key: "templates",
                    label: "Templates",
                    icon: <LayoutTemplate className="size-4" />,
                    href: templatesHref,
                    active: isActive(templatesHref),
                },
            ],
        },
    ];

    return <SideNav ariaLabel="Primary navigation" linkComponent={Link} groups={groups} header={<WorkspacePicker />} />;
}
